import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";

interface TimelineChartProps {
  task: any;
  weeks: Array<{
    label: string;
    start: Date;
    end: Date;
  }>;
  startDate: Date;
  endDate: Date;
  level: number;
  showConnector?: boolean;
  viewMode: "today" | "weeks" | "months" | "quarters";
  onTaskClick?: (taskId: string) => void; // ✅ Add callback prop
}

export const TimelineChart: React.FC<TimelineChartProps> = ({
  task,
  weeks,
  startDate,
  endDate,
  level,
  showConnector = false,
  viewMode,
  onTaskClick,
}) => {
  const today = new Date();
  const theme = useTheme();
  const totalDays = Math.ceil(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  const { barPosition, barWidth, todayPosition, isVisible } = useMemo(() => {
    const taskStart = new Date(task.startDate);
    const taskEnd = new Date(task.endDate);

    const taskStartDay = Math.ceil(
      (taskStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    const taskEndDay = Math.ceil(
      (taskEnd.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    const todayDay = Math.ceil(
      (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    const isVisible = taskEndDay >= 0 && taskStartDay <= totalDays;
    const clampedStartDay = Math.max(0, taskStartDay);
    const clampedEndDay = Math.min(totalDays, taskEndDay);

    const barPosition = clampedStartDay * 40; // 40px per day
    const barWidth = (clampedEndDay - clampedStartDay) * 40;
    const todayPosition = todayDay * 40;

    return { barPosition, barWidth, todayPosition, isVisible };
  }, [task, startDate, endDate, totalDays]);

  // ✅ Handle bar click
  const handleBarClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    if (onTaskClick) {
      onTaskClick(task._id);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: 56,
        height: 56,
        width: `${totalDays * 40}px`,
        minWidth: `${totalDays * 40}px`,
      }}
    >
      {/* Day Grid Background - Full Width */}
      <Box
        sx={{
          display: "flex",
          height: "100%",
          width: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        {Array.from({ length: totalDays }, (_, i) => {
          const date = new Date(startDate);
          date.setDate(date.getDate() + i);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;

          return (
            <Box
              key={i}
              sx={{
                width: 40,
                height: "100%",
                // borderRight: "1px solid #F3F4F6",
                borderRight: `1px solid ${theme.palette.divider}`,
                // bgcolor: isWeekend ? "#F9FAFB" : "white",
                background: isWeekend
                  ? theme.palette.mode === "dark"
                    ? alpha(theme.palette.background.default, 0.5) // ✅ Dark mode weekend
                    : "#F9FAFB"
                  : theme.palette.background.paper,
              }}
            />
          );
        })}
      </Box>

      {/* Today Indicator Line */}
      {todayPosition >= 0 && todayPosition <= totalDays * 40 && (
        <Box
          sx={{
            position: "absolute",
            left: `${todayPosition}px`,
            top: 0,
            bottom: 0,
            width: 2,
            // bgcolor: "#3B82F6",
            background: theme.palette.primary.main,
            zIndex: 8,
            opacity: 0.8,
          }}
        />
      )}

      {/* Task Bar */}
      {isVisible && (
        <Box
          sx={{
            position: "absolute",
            left: `${barPosition}px`,
            width: `${Math.max(barWidth, 40)}px`,
            top: "50%",
            transform: "translateY(-50%)",
            px: 0.5,
            zIndex: 2,
          }}
        >
          <Tooltip
            title={
              <Box>
                <Typography variant="caption" fontWeight={600}>
                  {task.title}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ opacity: 0.9 }}
                >
                  {new Date(task.startDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}{" "}
                  -{" "}
                  {new Date(task.endDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </Typography>
                {task.assignees && task.assignees.length > 0 && (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {task.assignees.map((u: any) => u.fullName).join(", ")}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 0.5, fontSize: 10, opacity: 0.7 }}
                >
                  Click to view details
                </Typography>
              </Box>
            }
            arrow
          >
            <Box
              onClick={handleBarClick}
              sx={{
                height: task.type === "EPIC" ? 28 : 24,
                // background: task.color || "#6B7280",
                background: theme.palette.primary.main,
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                px: 1,
                // boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                // cursor: "pointer",
                // transition: "all 0.2s",
                // "&:hover": {
                //   opacity: 0.85,
                //   boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                //   transform: "scale(1.02)",
                // },
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 1px 2px rgba(0,0,0,0.3)" // ✅ Stronger shadow in dark mode
                    : "0 1px 2px rgba(0,0,0,0.1)",
                cursor: "pointer",
                transition: "all 0.2s",
                border:
                  theme.palette.mode === "dark"
                    ? `1px solid ${alpha("#fff", 0.1)}` // ✅ Add border in dark mode
                    : "none",
                "&:hover": {
                  opacity: 0.85,
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 2px 4px rgba(0,0,0,0.4)"
                      : "0 2px 4px rgba(0,0,0,0.15)",
                  transform: "scale(1.02)",
                },
              }}
            >
              {task.assignees && task.assignees.length > 0 && barWidth > 80 && (
                <AvatarGroup
                  max={3}
                  sx={{
                    "& .MuiAvatar-root": {
                      width: 18,
                      height: 18,
                      fontSize: 8,
                      // border: "1.5px solid white",
                      border: `1.5px solid ${theme.palette.background.paper}`,
                    },
                  }}
                >
                  {task.assignees.map((user: any) => (
                    <Avatar
                      key={user._id}
                      sx={{ width: 18, height: 18, fontSize: 8 }}
                    >
                      {user.fullName
                        .split(" ")
                        .map((n: string) => n[0])
                        .join("")
                        .toUpperCase()}
                    </Avatar>
                  ))}
                </AvatarGroup>
              )}
            </Box>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};
